import { type ReactNode, useEffect } from 'react';
import { Search, X } from 'lucide-react';

import { searchOptionId } from '@/components/SearchPalette/searchOptionId';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import { cn } from '@/util/utils';

/** The prototype's four drawn states, and a failure it does not draw. */
type SearchPaletteState = 'prompt' | 'loading' | 'results' | 'empty' | 'failed';

interface SearchPaletteItem {
    key: string;
    label: string;
}

interface SearchPaletteGroup {
    key: string;
    label: string;
    icon: ReactNode;
    items: SearchPaletteItem[];
}

interface SearchPaletteProps {
    /** The listbox's id: the field it belongs to points at it, and each option's id is built from it. */
    id: string;
    state: SearchPaletteState;
    /** What was typed, echoed in the palette's own row; the palette never owns a second input. */
    query: string;
    placeholder: string;
    /** The prompt, empty or failed state's sentence. */
    message?: string;
    clearLabel: string;
    resultsLabel: string;
    groups: SearchPaletteGroup[];
    /** The highlighted option, counted across every group in reading order. */
    active: number;
    onActiveChange: (index: number) => void;
    onSelect: (index: number) => void;
    onClear: () => void;
    className?: string;
}

// READ 252:4115: five bars of uneven width, proportional rather than measured.
const LOADING_BARS = ['78%', '84%', '72%', '84%', '55%'];

/**
 * search-palette, READ 251:3606 with its siblings 252:3743 (keep typing), 252:4115 (loading) and
 * 251:3748 (no results): 420 wide, popover ground, 1px border, 10 radius, shadow md. The row at
 * the top echoes the field it hangs from rather than being an input of its own, which is the
 * design's call: all four frames draw the query sitting in the header.
 *
 * Driven from outside: the field keeps focus and owns the keyboard (the arrows move `active`,
 * Enter chooses it), so this only draws. Hovering moves the highlight, so the mouse and the
 * keyboard never disagree about what Enter would take, and pressing inside never takes focus
 * from the field.
 */
function SearchPalette({
    id,
    state,
    query,
    placeholder,
    message,
    clearLabel,
    resultsLabel,
    groups,
    active,
    onActiveChange,
    onSelect,
    onClear,
    className,
}: SearchPaletteProps) {
    // The highlighted option stays in view when the arrows walk past the list's edge.
    useEffect(() => {
        if (state !== 'results') return;
        document.getElementById(searchOptionId(id, active))?.scrollIntoView?.({ block: 'nearest' });
    }, [id, active, state]);

    // Where each group's options start when counted across the whole list.
    const starts = groups.map((_, g) =>
        groups.slice(0, g).reduce((count, group) => count + group.items.length, 0),
    );

    return (
        <div
            data-slot="search-palette"
            data-state={state}
            onMouseDown={(event) => event.preventDefault()}
            className={cn(
                'bg-popover text-popover-foreground border-border flex flex-col overflow-hidden rounded-lg border shadow-md',
                className,
            )}
        >
            <div className="border-border flex h-12 flex-none items-center gap-2 border-b p-3">
                <Search aria-hidden className="text-foreground size-4 flex-none opacity-50" />
                <span
                    className={cn(
                        'min-w-0 flex-1 truncate text-sm',
                        query ? 'text-foreground' : 'text-muted-foreground',
                    )}
                >
                    {query || placeholder}
                </span>
                <button
                    type="button"
                    aria-label={clearLabel}
                    onClick={onClear}
                    className="text-foreground focus-visible:ring-ring/50 -m-1 inline-flex flex-none items-center justify-center rounded-sm p-1 outline-none focus-visible:ring-2 pointer-coarse:-m-3 pointer-coarse:size-11 [&_svg]:size-4"
                >
                    <X aria-hidden />
                </button>
            </div>

            {state === 'loading' ? (
                <div className="flex flex-col gap-3 px-4 pt-3 pb-6">
                    {LOADING_BARS.map((width, i) => (
                        <Skeleton key={i} className="h-2.5 rounded-full" style={{ width }} />
                    ))}
                </div>
            ) : null}

            {state === 'prompt' || state === 'empty' || state === 'failed' ? (
                <p
                    role="status"
                    className={cn(
                        'text-muted-foreground text-sm',
                        state === 'prompt' ? 'px-4 pt-3 pb-4' : 'px-4 py-4 text-center',
                    )}
                >
                    {message}
                </p>
            ) : null}

            {state === 'results' ? (
                <div
                    id={id}
                    role="listbox"
                    aria-label={resultsLabel}
                    className="max-h-80 overflow-y-auto px-2 py-1"
                >
                    {groups.map((group, g) => (
                        <div
                            key={group.key}
                            role="group"
                            aria-labelledby={`${id}-${group.key}`}
                            className="[&+&]:mt-1"
                        >
                            <p
                                id={`${id}-${group.key}`}
                                className="text-muted-foreground flex h-7 items-center px-2 text-xs font-medium"
                            >
                                {group.label}
                            </p>
                            {group.items.map((item, i) => {
                                const position = starts[g] + i;
                                const on = position === active;
                                return (
                                    <div
                                        key={item.key}
                                        id={searchOptionId(id, position)}
                                        role="option"
                                        aria-selected={on}
                                        onMouseMove={() => {
                                            if (!on) onActiveChange(position);
                                        }}
                                        onClick={() => onSelect(position)}
                                        className={cn(
                                            'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-3 text-sm [&_svg]:size-4 [&_svg]:flex-none',
                                            on
                                                ? 'bg-accent text-accent-foreground'
                                                : 'text-foreground [&_svg]:text-muted-foreground',
                                        )}
                                    >
                                        {group.icon}
                                        <span className="min-w-0 flex-1 truncate">
                                            {item.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export {
    SearchPalette,
    type SearchPaletteGroup,
    type SearchPaletteItem,
    type SearchPaletteProps,
    type SearchPaletteState,
};
